import { ReactNode, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  formatCost,
  formatDate,
  formatNumber,
  formatRelativeTime,
  formatTimestamp,
} from '@/lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { isDev } from '@/lib/isDev';
import { getCachedData, invalidateCache, setCachedData } from '@/lib/cache';
import type {
  AdminTableCategory,
  AdminUserDetail,
  AdminUserSummaryResponse,
  AdminUserReferralsResponse,
} from '@/types/admin';
import { EmailViewDialog } from '@/components/EmailViewDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';

const EMAIL_TEMPLATES = [
  { value: 'welcome', label: 'Welcome' },
  {
    value: 'projects-oct-2025',
    label: 'Projects - Give your AI the full picture 📚',
  },
  {
    value: 'project-review-nov-2025',
    label: 'Project Review - Your AI editor reads the whole story 📖',
  },
  {
    value: 'referrals-dec-2025',
    label: 'Referrals - Share and Earn 🎁',
  },
  { value: 'cost-bug-12-may', label: 'Cost Bug 12 May' },
  { value: 'credits-bumped', label: 'Credits Bumped' },
];

const STRIPE_DASHBOARD_BASE_URL = !isDev
  ? 'https://dashboard.stripe.com/acct_1PceAtHkXE6ArGGn'
  : 'https://dashboard.stripe.com/acct_1PceAtHkXE6ArGGn/test';

export default function ViewUserPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AdminUserSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [template, setTemplate] = useState(
    EMAIL_TEMPLATES[0]?.value ?? 'welcome',
  );
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<{
    userId: string;
    eventId: string;
  } | null>(null);
  const [bumpAmount, setBumpAmount] = useState('0.1');
  const [bumping, setBumping] = useState(false);
  const [bumpResult, setBumpResult] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState(false);
  const [tagValue, setTagValue] = useState('');
  const [updatingTag, setUpdatingTag] = useState(false);
  const [tagUpdateResult, setTagUpdateResult] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<AdminUserReferralsResponse | null>(
    null,
  );
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{
    success: boolean;
    message: string;
    timedOut?: boolean;
    report?: {
      totalDynamoDBRecords: number;
      totalS3Objects: number;
      errors: string[];
    };
  } | null>(null);
  const navigate = useNavigate();

  const subscriptionId =
    (user?.subscriptionId ??
      summary?.user?.subscriptionId ??
      summary?.subscription?.subscriptionId ??
      null) ||
    null;
  const subscriptionStatus =
    (summary?.subscription?.status &&
    typeof summary.subscription.status === 'string'
      ? summary.subscription.status
      : null) ?? null;
  const subscriptionPeriodEnd = getSubscriptionPeriodEnd(summary);
  const stripeSubscriptionUrl = subscriptionId
    ? `${STRIPE_DASHBOARD_BASE_URL}/subscriptions/${subscriptionId}`
    : null;

  const shouldShowSummaryCard =
    summaryLoading || summaryError !== null || summary !== null;
  const shouldShowAiExecCard = summaryLoading || summary !== null;
  const summaryMonthlyCost = getOptionalNumber(summary?.user?.cost);
  const summaryPackCost = getOptionalNumber(summary?.user?.packCreditsCost);
  const summaryTotalCost =
    summaryMonthlyCost === null && summaryPackCost === null
      ? null
      : (summaryMonthlyCost ?? 0) + (summaryPackCost ?? 0);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchUser() {
      try {
        // Check cache first
        const cacheKey = `view_${userId}`;
        const cachedData = getCachedData<AdminUserDetail>(cacheKey);

        if (cachedData) {
          setUser(cachedData);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        const response = await authenticatedFetch(`users/${userId}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load user (${response.status})`);
        }

        const payload = (await response.json()) as AdminUserDetail;
        if (!controller.signal.aborted) {
          setUser(payload);
          // Store in cache
          setCachedData(cacheKey, payload);
        }
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to load user details.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchUser();

    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchSummary() {
      try {
        // Check cache first
        const cacheKey = `view_${userId}_summary`;
        const cachedData = getCachedData<AdminUserSummaryResponse>(cacheKey);

        if (cachedData) {
          setSummary(cachedData);
          setSummaryLoading(false);
          return;
        }

        setSummaryLoading(true);
        setSummaryError(null);
        const response = await authenticatedFetch(`users/${userId}/summary`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load user summary (${response.status})`);
        }

        const payload = (await response.json()) as AdminUserSummaryResponse;
        if (!controller.signal.aborted) {
          setSummary(payload);
          // Store in cache
          setCachedData(cacheKey, payload);
        }
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setSummaryError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to load user summary.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setSummaryLoading(false);
        }
      }
    }

    fetchSummary();

    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchReferrals() {
      try {
        // Check cache first
        const cacheKey = `view_${userId}_referrals`;
        const cachedData = getCachedData<AdminUserReferralsResponse>(cacheKey);

        if (cachedData) {
          setReferrals(cachedData);
          setReferralsLoading(false);
          return;
        }

        setReferralsLoading(true);
        setReferralsError(null);
        const response = await authenticatedFetch(`users/${userId}/referrals`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load referrals (${response.status})`);
        }

        const payload = (await response.json()) as AdminUserReferralsResponse;
        if (!controller.signal.aborted) {
          setReferrals(payload);
          // Store in cache
          setCachedData(cacheKey, payload);
        }
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setReferralsError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to load referrals.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setReferralsLoading(false);
        }
      }
    }

    fetchReferrals();

    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  const handleSendEmail = async () => {
    if (!userId) return;

    try {
      setSending(true);
      setSendResult(null);
      const response = await authenticatedFetch('sendEmail', {
        method: 'POST',
        body: JSON.stringify({ userId, template }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message =
          typeof body?.error === 'string'
            ? body.error
            : `Failed to send email (${response.status})`;
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const message =
        typeof result?.message === 'string'
          ? result.message
          : 'Email send request queued.';
      setSendResult(message);

      // Invalidate cache for this user after successful email send
      invalidateCache(`view_${userId}`);
      invalidateCache(`view_${userId}_summary`);
    } catch (sendError) {
      console.error(sendError);
      setSendResult(
        sendError instanceof Error
          ? sendError.message
          : 'Unable to send email.',
      );
    } finally {
      setSending(false);
    }
  };

  const handleBumpLimit = async () => {
    if (!userId) return;

    try {
      setBumping(true);
      setBumpResult(null);
      const response = await authenticatedFetch('bumpLimit', {
        method: 'POST',
        body: JSON.stringify({ userId, amount: parseFloat(bumpAmount) }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message =
          typeof body?.error === 'string'
            ? body.error
            : `Failed to bump limit (${response.status})`;
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));
      const newLimit = result?.newLimit;
      const message =
        typeof newLimit === 'number'
          ? `Limit bumped successfully! New limit: ${newLimit.toFixed(2)}`
          : 'Limit bumped successfully!';
      setBumpResult(message);

      // Invalidate cache for this user after successful bump
      invalidateCache(`view_${userId}`);
      invalidateCache(`view_${userId}_summary`);
    } catch (bumpError) {
      console.error(bumpError);
      setBumpResult(
        bumpError instanceof Error
          ? bumpError.message
          : 'Unable to bump limit.',
      );
    } finally {
      setBumping(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!userId) return;

    try {
      setUpdatingTag(true);
      setTagUpdateResult(null);
      const response = await authenticatedFetch(`users/${userId}/tag`, {
        method: 'PUT',
        body: JSON.stringify({ tag: tagValue.trim() }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message =
          typeof body?.error === 'string'
            ? body.error
            : `Failed to update tag (${response.status})`;
        throw new Error(message);
      }

      setTagUpdateResult('Tag updated successfully!');
      setEditingTag(false);

      // Invalidate cache and refresh user data
      invalidateCache(`view_${userId}`);
      invalidateCache(`view_${userId}_summary`);

      // Update local user state
      if (user) {
        setUser({ ...user, tag: tagValue.trim() || undefined });
      }
    } catch (updateError) {
      console.error(updateError);
      setTagUpdateResult(
        updateError instanceof Error
          ? updateError.message
          : 'Unable to update tag.',
      );
    } finally {
      setUpdatingTag(false);
    }
  };

  const startEditingTag = () => {
    setTagValue(user?.tag || '');
    setEditingTag(true);
    setTagUpdateResult(null);
  };

  const cancelEditingTag = () => {
    setEditingTag(false);
    setTagValue('');
    setTagUpdateResult(null);
  };

  const handleDeleteUserData = async () => {
    if (!userId) return;

    try {
      setDeleting(true);
      setDeleteResult(null);
      const response = await authenticatedFetch(`users/${userId}/data`, {
        method: 'DELETE',
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          typeof body?.error === 'string'
            ? body.error
            : `Failed to delete user data (${response.status})`;
        throw new Error(message);
      }

      // Check if the operation timed out but made progress
      const timedOut = body?.report?.timedOut === true;

      setDeleteResult({
        success: !timedOut,
        message: body?.message || 'User data deleted successfully',
        timedOut,
        report: body?.report
          ? {
              totalDynamoDBRecords: body.report.totalDynamoDBRecords,
              totalS3Objects: body.report.totalS3Objects,
              errors: body.report.errors || [],
            }
          : undefined,
      });

      // Invalidate all caches for this user
      invalidateCache(`view_${userId}`);
      invalidateCache(`view_${userId}_summary`);
      invalidateCache(`view_${userId}_referrals`);
    } catch (deleteError) {
      console.error(deleteError);
      // Check for network timeout errors
      const errorMessage =
        deleteError instanceof Error ? deleteError.message : '';
      const isNetworkTimeout =
        errorMessage.includes('timeout') ||
        errorMessage.includes('network') ||
        errorMessage.includes('Failed to fetch');

      setDeleteResult({
        success: false,
        message: isNetworkTimeout
          ? 'Request timed out. Some data may have been deleted. Click "Try Again" to continue.'
          : deleteError instanceof Error
            ? deleteError.message
            : 'Unable to delete user data.',
        timedOut: isNetworkTimeout,
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const summaryDescription: ReactNode = summaryLoading ? (
    'Loading usage summary…'
  ) : summaryError ? (
    <span className="text-destructive">{summaryError}</span>
  ) : summary ? (
    <span className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="uppercase">
        {summary.environment}
      </Badge>
      <span>Generated {formatDate(summary.generatedAt)}</span>
    </span>
  ) : (
    'Summary not available yet.'
  );

  const aiExecDescription: ReactNode = summaryLoading ? (
    'Loading recent AI executions…'
  ) : summary ? (
    summary.aiExecutions.error ? (
      <span className="text-destructive">{summary.aiExecutions.error}</span>
    ) : summary.aiExecutions.recent.length > 0 ? (
      `Showing ${summary.aiExecutions.recent.length} of ${summary.aiExecutions.totalCount.toLocaleString()} recorded executions.`
    ) : (
      'No recent executions found for this account.'
    )
  ) : (
    'Recent execution data will appear once the summary loads.'
  );

  const metrics = summary
    ? [
        {
          label: 'Total records',
          value: formatNumber(summary.totals.totalRecords),
        },
        {
          label: 'Document chats',
          value: formatNumber(summary.totals.insights.documentChats),
        },
        {
          label: 'AI chats',
          value: formatNumber(summary.totals.insights.aiChats),
        },
        {
          label: 'Snippets',
          value: formatNumber(summary.totals.insights.snippets),
        },
        {
          label: 'AI executions',
          value: formatNumber(summary.aiExecutions.totalCount),
        },
      ]
    : [];

  return (
    <>
      <LoadingOverlay
        visible={summaryLoading}
        message="Loading user summary..."
      />
      <div className="space-y-3 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: user?.email || userId || 'Loading...' },
          ]}
        />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">User detail</h1>
          <p className="text-muted-foreground">
            Inspect account information and trigger admin-only actions.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>User information</CardTitle>
            <CardDescription>
              {loading && 'Loading user information…'}
              {error && <span className="text-destructive">{error}</span>}
              {!loading && !error && user && user.email}
            </CardDescription>
          </CardHeader>
          {user && (
            <CardContent className="grid gap-2 sm:gap-4 md:grid-cols-2">
              <InfoRow
                label="User ID"
                value={<span className="font-mono">{user.id}</span>}
              />
              <InfoRow
                label="Created"
                value={
                  user.createdAt ? formatTimestamp(Number(user.createdAt)) : '—'
                }
              />
              <InfoRow
                label="Last active"
                value={
                  user.lastActiveAt
                    ? formatTimestamp(Number(user.lastActiveAt))
                    : '—'
                }
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-muted-foreground">
                  Status
                </span>
                <div className="mt-1">
                  {user.status ? (
                    <Badge variant="secondary">{user.status}</Badge>
                  ) : (
                    <Badge variant="outline">unknown</Badge>
                  )}
                </div>
              </div>
              <InfoRow
                label="Subscription"
                value={
                  stripeSubscriptionUrl ? (
                    <a
                      href={stripeSubscriptionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline"
                    >
                      {subscriptionId}
                    </a>
                  ) : (
                    (subscriptionId ?? '—')
                  )
                }
              />
              <InfoRow
                label="Subscription status"
                value={
                  subscriptionStatus ? (
                    <Badge variant="secondary" className="capitalize">
                      {subscriptionStatus}
                    </Badge>
                  ) : (
                    '—'
                  )
                }
              />
              <InfoRow
                label="Subscription period end"
                value={formatTimestamp(subscriptionPeriodEnd)}
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-muted-foreground">
                  Tag
                </span>
                {!editingTag ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-base font-medium text-foreground">
                      {user.tag ? (
                        <Badge variant="outline">{user.tag}</Badge>
                      ) : (
                        '—'
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditingTag}
                      className="h-6 px-2 text-xs"
                    >
                      Edit
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      value={tagValue}
                      onChange={(e) => setTagValue(e.target.value)}
                      placeholder="Enter tag"
                      className="max-w-[200px]"
                      maxLength={50}
                      disabled={updatingTag}
                    />
                    <Button
                      size="sm"
                      onClick={handleUpdateTag}
                      disabled={updatingTag}
                      className="h-8"
                    >
                      {updatingTag ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEditingTag}
                      disabled={updatingTag}
                      className="h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {tagUpdateResult && (
                  <span className="mt-1 text-sm text-muted-foreground">
                    {tagUpdateResult}
                  </span>
                )}
              </div>
              {summary?.user && (
                <>
                  <InfoRow
                    label="Cost (current period)"
                    value={
                      summaryTotalCost !== null
                        ? formatCost(summaryTotalCost)
                        : '—'
                    }
                  />
                  {summaryPackCost !== null && summaryPackCost > 0 && (
                    <>
                      <InfoRow
                        label="Monthly debited"
                        value={formatCost(summaryMonthlyCost ?? 0)}
                      />
                      <InfoRow
                        label="Pack debited"
                        value={formatCost(summaryPackCost)}
                      />
                    </>
                  )}
                  <InfoRow
                    label="Limit"
                    value={
                      typeof summary.user.limit === 'number'
                        ? formatCost(summary.user.limit)
                        : '—'
                    }
                  />
                  {typeof summary.user.previousUnusedSpend === 'number' && (
                    <InfoRow
                      label="Previous unused spend"
                      value={formatCost(summary.user.previousUnusedSpend)}
                    />
                  )}
                  <InfoRow
                    label="Total Limit"
                    value={
                      typeof summary.user.limit === 'number'
                        ? formatCost(
                            summary.user.limit +
                              (typeof summary.user.previousUnusedSpend ===
                              'number'
                                ? summary.user.previousUnusedSpend
                                : 0),
                          )
                        : '—'
                    }
                  />
                </>
              )}
              {summary?.contact && (
                <>
                  <InfoRow
                    label="Contact email"
                    value={summary.contact.email || '—'}
                  />
                  <InfoRow
                    label="Contact created"
                    value={
                      summary.contact.createdAt
                        ? formatTimestamp(summary.contact.createdAt)
                        : '—'
                    }
                  />
                  <InfoRow
                    label="Last login"
                    value={
                      summary.contact.lastLoginAt
                        ? formatTimestamp(summary.contact.lastLoginAt)
                        : '—'
                    }
                  />
                </>
              )}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Referral Information</CardTitle>
            <CardDescription>
              {referralsLoading && 'Loading referral data…'}
              {referralsError && (
                <span className="text-destructive">{referralsError}</span>
              )}
              {!referralsLoading &&
                !referralsError &&
                referrals &&
                'Referral code, rewards, and referred users'}
            </CardDescription>
          </CardHeader>
          {referrals && (
            <CardContent className="space-y-6">
              <div className="grid gap-2 sm:gap-4 md:grid-cols-2">
                <InfoRow
                  label="Referral Code"
                  value={
                    referrals.referralCode ? (
                      <span className="font-mono text-lg">
                        {referrals.referralCode}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow
                  label="Total Referral Rewards"
                  value={
                    referrals.totalReferralRewards > 0
                      ? `${referrals.totalReferralRewards.toLocaleString()} credits`
                      : '0 credits'
                  }
                />
                {referrals.referredByUserId && (
                  <>
                    <InfoRow
                      label="Referred By User ID"
                      value={
                        <span className="font-mono text-xs">
                          {referrals.referredByUserId}
                        </span>
                      }
                    />
                    <InfoRow
                      label="Referred By Code"
                      value={referrals.referredByCode || '—'}
                    />
                  </>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  Referral Statistics
                </h3>
                <div className="grid gap-2 sm:gap-4 grid-cols-3">
                  <div className="flex flex-col items-center p-3 border rounded-md">
                    <span className="text-2xl font-bold">
                      {referrals.stats.referralCount}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Referrals
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-3 border rounded-md">
                    <span className="text-2xl font-bold">
                      {referrals.stats.convertingCount}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Converting
                    </span>
                  </div>
                  <div className="flex flex-col items-center p-3 border rounded-md">
                    <span className="text-2xl font-bold">
                      {referrals.stats.convertedCount}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Converted
                    </span>
                  </div>
                </div>
              </div>

              {referrals.referrals.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">
                    Referred Users ({referrals.totalReferrals})
                  </h3>
                  <div className="overflow-hidden rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User ID</TableHead>
                          <TableHead>State</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Subscribed</TableHead>
                          <TableHead>Converted</TableHead>
                          <TableHead className="text-right">Reward</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {referrals.referrals.map((referral, index) => (
                          <TableRow key={referral.referralUserId || index}>
                            <TableCell className="font-mono text-xs">
                              {referral.referralUserId}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  referral.referralState === 'converted'
                                    ? 'default'
                                    : referral.referralState === 'converting'
                                      ? 'secondary'
                                      : 'outline'
                                }
                              >
                                {referral.referralState}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatTimestamp(referral.createdAt)}
                            </TableCell>
                            <TableCell className="text-xs">
                              {referral.convertingAt
                                ? formatTimestamp(referral.convertingAt)
                                : '—'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {referral.convertedAt
                                ? formatTimestamp(referral.convertedAt)
                                : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {referral.rewardAmount
                                ? `${referral.rewardAmount.toLocaleString()} cr`
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No referrals yet
                </p>
              )}
            </CardContent>
          )}
        </Card>

        {shouldShowSummaryCard && (
          <Card>
            <CardHeader>
              <CardTitle>Usage summary</CardTitle>
              <CardDescription>{summaryDescription}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-6">
              {!summaryLoading && summary && (
                <div className="grid gap-2 sm:gap-4 sm:grid-cols-2 xl:grid-cols-5">
                  {metrics.map((metric) => (
                    <SummaryMetric
                      key={metric.label}
                      label={metric.label}
                      value={metric.value}
                    />
                  ))}
                </div>
              )}
              {!summaryLoading && summary && summary.tables.length > 0 && (
                <div className="overflow-hidden rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dataset</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.tables
                        .filter(
                          (table) =>
                            ![
                              'AVATAR_CHARACTERS',
                              'BOOKS',
                              'BOOK_CHAPTERS',
                              'COMMENTS',
                            ].includes(table.key),
                        )
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((table) => (
                          <TableRow key={table.key}>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium leading-tight">
                                    {table.label}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="uppercase"
                                  >
                                    {formatCategory(table.category)}
                                  </Badge>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {table.tableName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(table.count)}
                            </TableCell>
                            <TableCell>
                              {table.error ? (
                                <span className="text-xs text-destructive">
                                  {table.error}
                                </span>
                              ) : (
                                <Badge variant="secondary">OK</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                    {summary.tables.length > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TableCell>Total (without errors)</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(summary.totals.totalRecords)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </div>
              )}
              {!summaryLoading && summary && summary.tables.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No DynamoDB records were found for this user yet.
                </p>
              )}
              {!summaryLoading && !summary && !summaryError && (
                <p className="text-sm text-muted-foreground">
                  Summary data will load once available.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {shouldShowAiExecCard && (
          <Card>
            <CardHeader>
              <CardTitle>Recent AI executions</CardTitle>
              <CardDescription>{aiExecDescription}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-4">
              {!summaryLoading &&
                summary &&
                summary.aiExecutions.recent.length > 0 && (
                  <div className="overflow-hidden rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">
                            Tokens In
                          </TableHead>
                          <TableHead className="text-right">
                            Tokens Out
                          </TableHead>
                          <TableHead className="text-right">Cached</TableHead>
                          <TableHead className="text-right">
                            Reasoning
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            Execution ID
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.aiExecutions.recent.map((record, index) => (
                          <TableRow key={record.id ?? index}>
                            <TableCell className="font-mono">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeTime(record.timestamp)}
                                </span>
                                <span className="text-xs">
                                  {formatTimestamp(record.timestamp)}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>{record.modelId ?? '—'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCost(record.cost)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(record.tokensIn)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(record.tokensOut)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(record.tokensCached)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatNumber(record.tokensReasoning)}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell font-mono text-xs">
                              {record.id ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={2}>Totals (shown)</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCost(summary.aiExecutions.totals.cost)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(summary.aiExecutions.totals.tokensIn)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(
                              summary.aiExecutions.totals.tokensOut,
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(
                              summary.aiExecutions.totals.tokensCached,
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(
                              summary.aiExecutions.totals.tokensReasoning,
                            )}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell" />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                )}
              {!summaryLoading &&
                summary &&
                summary.aiExecutions.recent.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    There are no recent AI executions recorded for this user
                    yet.
                  </p>
                )}
              {summaryLoading && (
                <p className="text-sm text-muted-foreground">
                  Loading execution activity…
                </p>
              )}
              {!summaryLoading && summary && (
                <div className="text-sm">
                  <Link
                    to={`/users/${userId}/ai-executions`}
                    className="text-primary hover:underline"
                  >
                    View full AI execution history →
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Send email</CardTitle>
            <CardDescription>
              Trigger transactional templates directly from the admin console.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 sm:space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template">Template</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger id="template">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      <span>{item.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button onClick={handleSendEmail} disabled={sending || !userId}>
                {sending ? 'Sending…' : 'Send email'}
              </Button>
              {sendResult && (
                <span className="text-sm text-muted-foreground">
                  {sendResult}
                </span>
              )}
            </div>
            <div className="pt-2 border-t">
              <Link
                to={`/users/${userId}/send-email`}
                className="text-primary hover:underline text-sm"
              >
                Send custom email →
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bump limit</CardTitle>
            <CardDescription>
              Increase the user's credit limit by a specified amount.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 sm:space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bumpAmount">Amount</Label>
              <Input
                id="bumpAmount"
                type="number"
                step="0.01"
                min="0.01"
                value={bumpAmount}
                onChange={(e) => setBumpAmount(e.target.value)}
                placeholder="0.1"
                className="max-w-[200px]"
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button onClick={handleBumpLimit} disabled={bumping || !userId}>
                {bumping ? 'Bumping…' : 'Bump limit'}
              </Button>
              {bumpResult && (
                <span className="text-sm text-muted-foreground">
                  {bumpResult}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {summary && (
          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>
                {summary.notifications.recent.length > 0
                  ? `Showing ${summary.notifications.recent.length} of ${summary.notifications.totalCount.toLocaleString()} notifications.`
                  : 'No notifications found for this user.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary.notifications.recent.length > 0 && (
                <div className="overflow-hidden rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Subject</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.notifications.recent.map(
                        (notification, index) => {
                          const isSent = notification.status === 'sent';
                          const isClickable =
                            isSent && userId && notification.eventId;

                          return (
                            <TableRow
                              key={index}
                              className={
                                isClickable
                                  ? 'cursor-pointer hover:bg-muted/50'
                                  : undefined
                              }
                              onClick={() => {
                                if (
                                  isClickable &&
                                  userId &&
                                  notification.eventId
                                ) {
                                  setSelectedNotification({
                                    userId,
                                    eventId: notification.eventId,
                                  });
                                }
                              }}
                            >
                              <TableCell className="font-mono">
                                {notification.created
                                  ? new Date(
                                      notification.created * 1000,
                                    ).toLocaleString()
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {notification.status ? (
                                  <Badge variant="secondary">
                                    {notification.status}
                                  </Badge>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell>
                                {notification.error ? (
                                  <span className="text-xs text-destructive">
                                    {notification.error}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                              <TableCell className="font-mono">
                                {notification.eventId || '—'}
                              </TableCell>
                              <TableCell>
                                {notification.subject || '—'}
                                {isClickable && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    (click to view)
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {summary && summary.pageNavigation && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Page navigation</CardTitle>
                  <CardDescription>
                    {summary.pageNavigation.recent.length > 0
                      ? `Showing ${summary.pageNavigation.recent.length} of ${summary.pageNavigation.totalCount.toLocaleString()} activity events.`
                      : 'No activity events found for this user.'}
                  </CardDescription>
                </div>
                {summary.pageNavigation.totalCount > 10 && (
                  <Button variant="outline" asChild>
                    <Link to={`/users/${userId}/navigation`}>View all</Link>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-4">
              {summary.pageNavigation.recent.length > 0 && (
                <div className="overflow-hidden rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Operation / Path</TableHead>
                        <TableHead>Phase / Status</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>IP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.pageNavigation.recent.map((record, index) => {
                        const fullPath = `${record.pathname || ''}${record.search || ''}`;
                        const isBackendEvent =
                          record.eventType === 'backend_operation';
                        const operationOrPath =
                          record.operation || fullPath || '—';
                        const phaseStatus = [record.eventPhase, record.status]
                          .filter((value) => !!value)
                          .join(' / ');
                        return (
                          <TableRow key={index}>
                            <TableCell className="font-mono">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-muted-foreground">
                                  {formatRelativeTime(record.created)}
                                </span>
                                <span className="text-xs">
                                  {record.created
                                    ? new Date(record.created).toLocaleString()
                                    : '—'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={isBackendEvent ? 'default' : 'outline'}
                              >
                                {isBackendEvent ? 'Backend' : 'Page'}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-sm truncate font-mono text-xs">
                              {operationOrPath}
                            </TableCell>
                            <TableCell className="max-w-xs truncate font-mono text-xs">
                              {phaseStatus || '—'}
                            </TableCell>
                            <TableCell>
                              {record.country ? (
                                <Badge variant="outline">
                                  {record.country}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {record.ipAddress || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="text-sm">
                <Link
                  to={`/users/${userId}/navigation`}
                  className="text-primary hover:underline"
                >
                  View full navigation history →
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedNotification && (
          <EmailViewDialog
            userId={selectedNotification.userId}
            eventId={selectedNotification.eventId}
            open={!!selectedNotification}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedNotification(null);
              }
            }}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>Debug tools</CardTitle>
            <CardDescription>
              Drill-down pages for incident investigation — each opens in a separate tab.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  to: `/users/${userId}/cost-audit`,
                  label: 'Cost Audit',
                  description: 'Reconcile debited credits vs AI execution costs. Surface over/under-charge.',
                },
                {
                  to: `/users/${userId}/payments`,
                  label: 'Payment History',
                  description: 'All Stripe payment events: subscriptions, packs, refunds.',
                },
                {
                  to: `/users/${userId}/ai-executions`,
                  label: 'AI Executions',
                  description: 'Full paginated history of AI calls with cost and token breakdown.',
                },
                {
                  to: `/users/${userId}/chats`,
                  label: 'Chats',
                  description: 'AI chats and character chats metadata (aichats-table, chats-table).',
                },
                {
                  to: `/users/${userId}/projects`,
                  label: 'Projects',
                  description: 'Writing projects with document counts and verification status.',
                },
                {
                  to: `/users/${userId}/documents`,
                  label: 'Documents',
                  description: 'Document metadata: type, size, project assignment (snippets-table).',
                },
                {
                  to: `/users/${userId}/logs`,
                  label: 'CloudWatch Logs',
                  description: 'Proxy CloudWatch logs filtered by userId with time range selector.',
                },
                {
                  to: `/users/${userId}/navigation`,
                  label: 'Navigation History',
                  description: 'Page-level and backend operation events with IP and country.',
                },
              ].map(({ to, label, description }) => (
                <a
                  key={to}
                  href={to}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors group"
                >
                  <div className="font-medium text-sm group-hover:text-primary">{label} ↗</div>
                  <div className="text-xs text-muted-foreground mt-1">{description}</div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
            <CardDescription>
              Permanently delete all user data for GDPR compliance. This action
              cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4">
              <div className="text-sm text-muted-foreground mb-3">
                Before deleting user data, you must set the user's tag to{' '}
                <Badge variant="outline" className="font-mono">
                  delete
                </Badge>{' '}
                as a safety measure. This ensures intentional deletion and
                prevents accidental data loss.
              </div>
              <div className="text-sm text-muted-foreground">
                Current tag:{' '}
                {user?.tag ? (
                  <Badge
                    variant={user.tag === 'delete' ? 'destructive' : 'outline'}
                  >
                    {user.tag}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">none</span>
                )}
              </div>
            </div>

            {deleteResult && (
              <div
                className={`rounded-md border p-4 ${
                  deleteResult.success
                    ? 'border-green-500/50 bg-green-500/5'
                    : deleteResult.timedOut
                      ? 'border-yellow-500/50 bg-yellow-500/5'
                      : 'border-destructive/50 bg-destructive/5'
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    deleteResult.success
                      ? 'text-green-600'
                      : deleteResult.timedOut
                        ? 'text-yellow-600'
                        : 'text-destructive'
                  }`}
                >
                  {deleteResult.message}
                </p>
                {deleteResult.report && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    <p>
                      DynamoDB records deleted:{' '}
                      {deleteResult.report.totalDynamoDBRecords.toLocaleString()}
                    </p>
                    <p>
                      S3 objects deleted:{' '}
                      {deleteResult.report.totalS3Objects.toLocaleString()}
                    </p>
                    {deleteResult.report.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-destructive">Errors:</p>
                        <ul className="list-disc list-inside">
                          {deleteResult.report.errors.map((error, index) => (
                            <li key={index} className="text-destructive">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {deleteResult.success && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => navigate('/users')}
                  >
                    Return to Users List
                  </Button>
                )}
                {deleteResult.timedOut && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setDeleteResult(null);
                      setShowDeleteDialog(true);
                    }}
                  >
                    Try Again
                  </Button>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={
                  !userId ||
                  user?.tag !== 'delete' ||
                  deleting ||
                  (deleteResult?.success && !deleteResult?.timedOut)
                }
              >
                {deleting ? 'Deleting...' : 'Delete All User Data'}
              </Button>
              {user?.tag !== 'delete' && !deleteResult?.timedOut && (
                <span className="text-sm text-muted-foreground">
                  Set tag to "delete" to enable this action
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">
                Confirm User Data Deletion
              </DialogTitle>
              <DialogDescription>
                You are about to permanently delete all data for user:
              </DialogDescription>
              <DialogClose onClose={() => setShowDeleteDialog(false)} />
            </DialogHeader>
            <div className="p-6 pt-0 space-y-4">
              <div className="rounded-md border p-3 bg-muted/40">
                <p className="font-mono text-sm break-all">{userId}</p>
                {user?.email && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {user.email}
                  </p>
                )}
              </div>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>This will delete:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All DynamoDB records across all tables</li>
                  <li>All S3 objects (documents, files, AI executions)</li>
                  <li>Referral relationships</li>
                  <li>User account and contact information</li>
                </ul>
              </div>
              <p className="text-sm font-semibold text-destructive">
                This action cannot be undone.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteUserData}
                  disabled={deleting}
                  className="flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete User Data'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

type InfoRowProps = {
  label: string;
  value: ReactNode;
};

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-base font-medium text-foreground">{value}</span>
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-muted/40 p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function getDisplayName(user: AdminUserDetail) {
  if (user.fullName) {
    return user.fullName;
  }

  const names = [user.firstName, user.lastName].filter(Boolean);
  return names.length > 0 ? names.join(' ') : '—';
}

function formatTokenPair(
  tokensIn: number | null | undefined,
  tokensOut: number | null | undefined,
) {
  const hasIn = typeof tokensIn === 'number' && !Number.isNaN(tokensIn);
  const hasOut = typeof tokensOut === 'number' && !Number.isNaN(tokensOut);

  if (!hasIn && !hasOut) {
    return '—';
  }

  return `${formatNumber(tokensIn ?? 0)} / ${formatNumber(tokensOut ?? 0)}`;
}

function formatCategory(category: AdminTableCategory) {
  switch (category) {
    case 'primary':
      return 'Primary';
    case 'history':
      return 'History';
    default:
      return 'Other';
  }
}

function getOptionalNumber(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
}

function getSubscriptionPeriodEnd(
  summary: AdminUserSummaryResponse | null,
): number | null {
  const userValue = summary?.user?.subscriptionPeriodEnd;
  if (typeof userValue === 'number') {
    return userValue;
  }

  const subscriptionValue = summary?.subscription?.subscriptionPeriodEnd;
  if (typeof subscriptionValue === 'number') {
    return subscriptionValue;
  }

  const currentPeriodEnd = summary?.subscription?.currentPeriodEnd;
  if (typeof currentPeriodEnd === 'number') {
    return currentPeriodEnd;
  }

  return null;
}
